import React, { MouseEvent } from 'react'
import styled from 'styled-components'

import { TooltipText } from '../../Step/types'
import { ButtonLoading } from './ButtonLoading'
import { ButtonNotification } from './ButtonNotification'
import { ButtonColors, getButtonColors } from './ButtonColors'
import {
  getBackground,
  getBorderColor,
  getButtonTooltipText,
  getColor,
  getHoverBackground
} from './ButtonService'
import { Tooltip, TooltipPlacement } from '../../Tooltip'

export enum ButtonType {
  DEFAULT = 'DEFAULT',
  PRIMARY = 'PRIMARY'
}

interface Props {
  text: any
  dataTest?: string
  disabled?: boolean
  isActive?: boolean
  className?: string
  isLoading?: boolean
  tooltipText?: TooltipText
  tooltipPlacement?: TooltipPlacement
  notificationText?: number | string
  buttonType?: ButtonType
  error?: TooltipText
  onClick: (event: React.MouseEvent<HTMLElement>) => void
  onClickWhenDisabled?: (event: React.MouseEvent<HTMLElement>) => void
}

export const Button: React.FC<Props> = ({
  text,
  error,
  dataTest,
  disabled,
  isActive,
  className,
  isLoading,
  buttonType,
  tooltipText,
  tooltipPlacement,
  notificationText,
  onClick,
  onClickWhenDisabled,
  ...rest
}) => {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      onClickWhenDisabled?.(event)
      return
    }

    onClick(event)
  }

  return (
    <Tooltip placement={tooltipPlacement} title={getButtonTooltipText(error, tooltipText)}>
      <StyledButton
        isDisabled={disabled} // this is because tooltip was causing some problems when using native disabled
        isActive={isActive}
        data-test={dataTest}
        className={className}
        onClick={handleClick}
        buttonColors={getButtonColors(buttonType, !!error)}
        {...rest}
      >
        <ButtonLoading isLoading={isLoading} hasText={!!text} />
        <ButtonNotification notificationText={notificationText} />
        {text}
      </StyledButton>
    </Tooltip>
  )
}

export interface WrapperProps {
  isActive?: boolean
  isDisabled?: boolean
  buttonColors: ButtonColors
}

const StyledButton = styled.button<WrapperProps>`
  min-width: 36px;
  height: 36px;
  padding: 2px 24px;
  display: inline-block;
  position: relative;
  white-space: nowrap;
  border: 1px solid ${getBorderColor};
  border-radius: 4px;
  font-weight: bold;
  -webkit-font-smoothing: antialiased;
  background: ${getBackground};
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
  color: ${getColor};
  text-align: center;

  &:hover {
    background: ${getHoverBackground};
    border: 1px solid ${props => getBorderColor(props, true)};
  }
`
